/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import PricingForm from "components/forms/PricingForm";
import Box from "components/generals/Box";
import Button from "components/generals/Button";
import ButtonDialog from "components/generals/ButtonDialog";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { CoreAPI_URLs } from "constants/http.const";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import useHttp from "hooks/http.hook";
import { TableProps } from "interfaces";
import { HeaderEnum, PricingInterface } from "medicalsystem-shared";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { getNewPricingDataContent } from "utils/model.utils";
import { formatPrice } from "utils/number.utils";

/**
 * Pricing table component for displaying and managing service pricing records.
 * 
 * Features:
 * - Displays pricing information in sortable data table
 * - Columns: CPT code, service name, price, action buttons
 * - Formatted price display with currency formatting
 * - Edit pricing functionality via modal with PricingForm
 * - Delete pricing with confirmation dialog and list update
 * - Permission-based access control for all operations
 * - Sortable by CPT code, name, and price
 * 
 * Pricing Types:
 * - Medical procedures
 * - Consultations
 * - Lab tests
 * - Imaging services
 * - Treatments
 * - Medical supplies
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates entity permissions before delete operations
 * 
 * @param {TableProps<PricingInterface>} props - Table properties
 * @param {PricingInterface[]} props.modelList - Array of pricing records
 * @param {Function} props.setModelList - Callback to update pricing list
 * 
 * @returns {JSX.Element} Pricing data table with formatted currency display
 */
const PricingTable: React.FC<TableProps<PricingInterface>> = ({
  modelList,
  setModelList,
  ...props
}) => {
  const [selectedPricing, setSelectedPricing] = useState<PricingInterface>(
    getNewPricingDataContent()
  );
  const [activeEditPricingModal, setActiveEditPricingModal] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<FormActionType>();
  const { t } = useTranslation();

  const [_isLoading, _error, sendRequest] = useHttp();

  const openModalHandler = (pricing: PricingInterface) => {
    setSelectedPricing(pricing);
    setActiveEditPricingModal(true);
  };

  const deleteHandler = (pricingTemplate: PricingInterface) => {
    sendRequest<boolean, PricingInterface>(
      CoreAPI_URLs.PRICING_GENERAL_OPERATIONS,
      onPricingTemplateDeleteHandler,
      "DELETE",
      JSON.stringify(pricingTemplate),
      [{ header: HeaderEnum.RESOURCE_ID, value: pricingTemplate.id }],
      { payload: pricingTemplate, successMessage: t("pricingTable.pricingDeleted") }
    );
  };

  const onPricingTemplateDeleteHandler = (result: boolean, pricingTemplate: PricingInterface) => {
    const indexList = modelList.indexOf(pricingTemplate);
    modelList.splice(indexList, 1);
    setModelList([...modelList]);
  };

  const getUITable = (list: PricingInterface[]) => {
    return {
      columns: [
        {
          Header: t("pricingTable.cpt"),
          accessor: "cpt",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("pricingTable.name"),
          accessor: "name",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("pricingTable.description"),
          accessor: "description",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("pricingTable.price"),
          accessor: "price",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={formatPrice(value)} />,
        },
        {
          Header: t("pricingTable.taxAmount"),
          accessor: "taxAmount",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={formatPrice(value)} />,
        },
        {
          Header: t("pricingTable.alternativeTaxAmount"),
          accessor: "alternativeTaxAmount",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={formatPrice(value)} />,
        },
        {
          Header: t("pricingTable.finalPrice"),
          accessor: "finalPrice",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={formatPrice(value)} />,
        },
        {
          Header: t("pricingTable.actions"),
          accessor: "id",
          disableSortBy: true,
          Cell: ({ value }: any) => (
            <>
              <Button
                name="viewPricingButton"
                variant="contained"
                color="info"
                sx={{ marginRight: 1 }}
                onClick={() => {
                  const pricing = list.find((p) => p.id === value);
                  if (pricing) {
                    setModalAction(FormActionType.MODIFY);
                    openModalHandler(pricing);
                  }
                }}
              >
                {t("pricingTable.viewAction")}
              </Button>

              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.PRICING].DELETE]}
              >
                <ButtonDialog
                  name="deletePricingButton"
                  onAcceptHandle={() => {
                    const pricing = list.find((p) => p.id === value);
                    if (pricing) {
                      deleteHandler(pricing);
                    }
                  }}
                  buttonText={t("pricingTable.deleteAction")}
                  title={t("pricingTable.deleteConfirmation")}
                  description={t("pricingTable.deleteDescription")}
                  labelledbyID={"PricingDelete"}
                  describedbyID={"PricingDescriptionId"}
                />
              </PermissionWrapper>
            </>
          ),
        },
      ],
      rows: list,
    };
  };

  const dataTableData = useMemo(() => getUITable(modelList), [modelList]);

  return (
    <>
      <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-end">
        <PermissionWrapper
          requiredPermissions={[PermissionsUser[EntityPermissions.PRICING].CREATE]}
        >
          <Button
            name="newPricingButton"
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              setModalAction(FormActionType.NEW);
              setSelectedPricing(getNewPricingDataContent);
              setActiveEditPricingModal(true);
            }}
          >
            {t("pricingForm.newPricing")}
          </Button>
        </PermissionWrapper>
      </Box>
      <DataTable
        {...props}
        table={dataTableData}
        canSearch
        isLoading={props.isLoading}
      />
      <Modal
        open={activeEditPricingModal}
        onClose={() => {
          setActiveEditPricingModal(false);
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            p: 2,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 800 }}>
            <PricingForm
              modelList={modelList}
              setModelList={setModelList}
              action={modalAction}
              model={selectedPricing}
              onCloseAction={() => setActiveEditPricingModal(false)}
              isModal={true}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default PricingTable;